import { Fragment, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { SlidersHorizontal } from 'lucide-react'
import type { OptionGroup, Tier } from '@/types'
import Card from '@/components/ui/Card'
import { Input } from '@/components/ui/Field'
import TierBadge from '@/components/ui/TierBadge'
import { cn } from '@/lib/cn'
import type { Draft, PatchDraft } from './draft'

/** The garment options this product offers, which are compulsory, and their add-ons per tier. */
export default function OptionsSection({
  draft,
  patch,
  groups,
  tiers,
}: {
  draft: Draft
  patch: PatchDraft
  groups: OptionGroup[]
  tiers: Tier[]
}) {
  const toggleOption = (key: string, value: string) => {
    const list = draft.options[key] ?? []
    patch({ options: { ...draft.options, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] } })
  }

  const toggleCompulsory = (key: string) =>
    patch({ optional: draft.optional.includes(key) ? draft.optional.filter((k) => k !== key) : [...draft.optional, key] })

  const setAddon = (key: string, value: string, tierId: number, amount: string) => {
    const values = draft.addons[key] ?? {}
    patch({ addons: { ...draft.addons, [key]: { ...values, [value]: { ...(values[value] ?? {}), [tierId]: amount } } } })
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-2xs text-muted sm:text-xs">
          {groups.length === 0
            ? 'No option groups yet - add them under Products > Product Options.'
            : 'Customers must choose a value in every group marked Compulsory before the item can be ordered. Untick it to make that group optional for this product.'}
        </p>
        <Link to="/admin/products/options" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          <SlidersHorizontal className="size-3.5" /> Manage Product Options
        </Link>
      </div>

      {groups.map((group) => {
        const listed = group.values.map((value) => value.value)
        const ticked = draft.options[group.key] ?? []
        // Ticked but gone from the list: shown so it can be unticked,
        // because the server refuses to save a value the list does not have.
        const stale = ticked.filter((value) => !listed.includes(value))
        return (
          <Card key={group.key}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-base font-medium">
                {group.label}
                {ticked.length > 0 && <span className="ml-1.5 text-xs font-normal text-muted">{ticked.length} offered</span>}
              </p>
              {/* Compulsory by default, and only meaningful once the product offers something here. */}
              {ticked.length > 0 && (
                <label className="flex items-center gap-2 text-xs font-medium text-foreground/80">
                  <input
                    type="checkbox"
                    checked={!draft.optional.includes(group.key)}
                    onChange={() => toggleCompulsory(group.key)}
                    className="size-4 accent-primary"
                  />
                  Compulsory
                </label>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {listed.map((value) => {
                const on = ticked.includes(value)
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleOption(group.key, value)}
                    className={cn(
                      'rounded-lg border px-2 py-1 text-xs font-medium',
                      on ? 'border-primary/60 bg-primary/20 text-primary' : 'border-foreground/20 text-foreground/60 hover:bg-foreground/5',
                    )}
                  >
                    {value}
                  </button>
                )
              })}
              {stale.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleOption(group.key, value)}
                  title="No longer on the Product Options list - click to untick it"
                  className="rounded-lg border border-warning/60 bg-warning/20 px-2 py-1 text-xs font-medium text-warning line-through"
                >
                  {value}
                </button>
              ))}
              {listed.length === 0 && stale.length === 0 && <span className="text-xs text-muted">No values in this group yet.</span>}
            </div>
            {ticked.length > 0 && (
              <div className="mt-3 rounded-lg border border-line p-2.5">
                <p className="mb-1.5 text-xs font-semibold text-foreground/70">Add-on price per piece (optional)</p>
                {/* One column per tier, like Tier prices. On a phone each
                    value takes its own line with the tier inputs under it,
                    so every tier stays on screen. */}
                <div className="overflow-x-auto">
                  <div
                    className="grid grid-cols-[repeat(var(--tiers),minmax(0,1fr))] items-center gap-x-2 gap-y-1.5 sm:grid-cols-[minmax(7rem,1fr)_repeat(var(--tiers),5.5rem)]"
                    style={{ '--tiers': tiers.length } as CSSProperties}
                  >
                    <span className="hidden sm:block" />
                    {tiers.map((tier) => (
                      <span key={tier.id} className="min-w-0 justify-self-start">
                        <TierBadge name={tier.name} color={tier.color} icon={tier.icon} />
                      </span>
                    ))}
                    {ticked.map((value) => (
                      <Fragment key={value}>
                        <span className="col-span-full truncate pt-1 text-xs font-medium sm:col-span-1 sm:pt-0">{value}</span>
                        {tiers.map((tier) => (
                          <Input
                            key={tier.id}
                            type="number"
                            min={0}
                            step="0.5"
                            placeholder="RM 0"
                            aria-label={`${value} add-on at ${tier.name}`}
                            value={draft.addons[group.key]?.[value]?.[tier.id] ?? ''}
                            onChange={(e) => setAddon(group.key, value, tier.id, e.target.value)}
                          />
                        ))}
                      </Fragment>
                    ))}
                  </div>
                </div>
                <p className="mt-1.5 text-2xs text-muted">
                  RM per piece on top of the price when a customer picks this option, by their tier. Leave blank for no
                  extra charge at that tier.
                </p>
              </div>
            )}
          </Card>
        )
      })}
    </div>
  )
}
